import { StrictMode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import App from "./App.js";
import { Login } from "./components/Login.js";
import { readToken, currentUser } from "./lib/auth.js";
import { applyTheme, readTheme } from "./lib/theme.js";
import { setUnauthorizedHandler } from "./api.js";
import "./bridge.css";
import "./styles.css";
import "./extra.css";
import "./jambix.css";

/** Signed in by the shared password, by Microsoft, or not yet known. */
type GateState = "checking" | "in" | "out";

/**
 * App is mounted only once the gate is open. Its effects fetch on mount, so
 * rendering it behind the gate would fire a screenful of requests at a closed
 * API and leave the user looking at load errors instead of a password field.
 *
 * Two doors open it. The shared password leaves a token this code can read,
 * so that one is known synchronously. A Microsoft session is an httpOnly
 * cookie, which by design this code cannot read - the only way to know it is
 * there is to ask the server, which is what "checking" is for.
 */
function Gate() {
  const [state, setState] = useState<GateState>(() => (readToken() ? "in" : "checking"));

  useEffect(() => {
    /* The server stops taking the token when the password changes under it.
       Dropping back to the gate is the only honest response - the alternative
       is a dashboard whose numbers quietly stop refreshing. */
    setUnauthorizedHandler(() => setState("out"));
  }, []);

  useEffect(() => {
    if (state !== "checking") return;
    let cancelled = false;
    void (async () => {
      const user = await currentUser();
      if (!cancelled) setState(user ? "in" : "out");
    })();
    return () => { cancelled = true; };
  }, [state]);

  /* App owns the theme once it is up, but the gate is on screen before that,
     and a reader who chose dark should not be handed a white page first. */
  useEffect(() => {
    if (state !== "in") applyTheme(readTheme());
  }, [state]);

  /* Nothing, rather than the login form, while the session check is in
     flight: showing the form to someone who is already signed in through
     Microsoft and then yanking it away reads as a glitch. It is one request. */
  if (state === "checking") return null;
  if (state === "out") return <Login onDone={() => setState("in")} />;
  return <App />;
}

const host = document.getElementById("root");
if (!host) throw new Error("#root not found");
createRoot(host).render(
  <StrictMode>
    <Gate />
  </StrictMode>,
);
